(function () {
  'use strict';

  const slotIds = new Set(['1', '2', '3', '4']);
  const paletteProperties = {
    background: '--tts-bg',
    backgroundEnd: '--tts-bg-end',
    text: '--tts-text',
    accent: '--tts-accent',
    button: '--tts-button',
    buttonText: '--tts-button-text'
  };
  const managedProperties = [...Object.values(paletteProperties), '--tts-image-desktop', '--tts-image-mobile'];
  const saleAliases = ['Back to School Sale', 'Fall Sale', 'Summer Sale', 'Sweet Dreams Sale', 'End of Summer Sale', 'Aussie Summer Sale', 'Spring Sale', 'Summer Splash Sale', 'Summer Send-Off', 'Summer offer', 'Prime Day Sale', 'Prime Day', 'Labor Day Sale', '4th of July Sale', 'Christmas Early Bird', 'Launch Offer'];

  function replaceSale(value, name, aliases) {
    const names = [...new Set(aliases.filter(alias => typeof alias === 'string' && alias.trim()).map(alias => alias.trim()))].sort((a, b) => b.length - a.length);
    if (!names.length) return value;
    const pattern = new RegExp('\\b(?:' + names.map(alias => alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')\\b', 'gi');
    return value.replace(pattern, match => match === match.toUpperCase() ? name.toUpperCase() : match === match.toLowerCase() ? name.toLowerCase() : name);
  }

  function validDate(value) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const [year, month, day] = value.split('-').map(Number);
    if (year < 1 || month < 1 || month > 12 || day < 1) return false;
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    return day <= [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  }

  function dateInTimezone(now, timezone) {
    if (typeof timezone !== 'string' || !timezone.trim() || !(now instanceof Date) || !Number.isFinite(now.getTime())) return null;
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        calendar: 'gregory',
        numberingSystem: 'latn',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).formatToParts(now);
      const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
      const date = `${values.year.padStart(4, '0')}-${values.month}-${values.day}`;
      return validDate(date) ? date : null;
    } catch (_) {
      return null;
    }
  }

  function resolveCampaign(config, now = new Date()) {
    const fallback = reason => ({ campaign: null, reason });
    if (!config || typeof config !== 'object') return fallback('invalid-config');
    const campaigns = Array.isArray(config.campaigns) ? config.campaigns.filter(campaign => campaign && typeof campaign === 'object') : [];
    const preview = config.designMode === true ? String(config.preview || 'auto') : 'auto';
    const explicitPreview = preview === 'default' || slotIds.has(preview);
    if (config.enabled !== true && !explicitPreview) return fallback('disabled');
    const mode = explicitPreview ? preview : String(config.mode || 'auto');
    if (mode === 'default' || mode === 'fallback') return fallback('default');
    if (slotIds.has(mode)) {
      const campaign = campaigns.find(candidate => String(candidate.id) === mode);
      return campaign ? { campaign, reason: explicitPreview ? 'preview' : 'manual' } : fallback('missing-campaign');
    }
    if (mode !== 'auto') return fallback('invalid-mode');
    const previewDate = config.designMode === true && typeof config.previewDate === 'string' ? config.previewDate.trim() : '';
    if (previewDate && !validDate(previewDate)) return fallback('invalid-preview-date');
    const today = previewDate || dateInTimezone(now, config.timezone);
    if (!today) return fallback('invalid-timezone-or-clock');
    const campaign = campaigns.find(candidate => candidate.enabled === true && validDate(candidate.start) && validDate(candidate.end) && candidate.start <= candidate.end && candidate.start <= today && today <= candidate.end);
    return campaign ? { campaign, reason: previewDate ? 'preview-date' : 'scheduled', date: today } : { ...fallback('no-match'), date: today };
  }

  function safeColor(value) {
    return typeof value === 'string' && /^#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i.test(value.trim()) ? value.trim() : null;
  }

  function safeImage(value, base) {
    if (typeof value !== 'string' || !value.trim() || value.length > 4096 || /[\u0000-\u001f\u007f]/.test(value)) return null;
    try {
      const url = new URL(value.trim(), base);
      return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? `url(${JSON.stringify(url.href)})` : null;
    } catch (_) {
      return null;
    }
  }

  function safeStoreUrl(value, base) {
    if (typeof value !== 'string' || !value.trim() || value.length > 4096 || /[\u0000-\u001f\u007f\\]/.test(value)) return null;
    try {
      const current = new URL(base);
      const url = new URL(value.trim(), current);
      if (!['https:', 'http:'].includes(url.protocol) || url.origin !== current.origin || url.username || url.password) return null;
      return url.pathname + url.search + url.hash;
    } catch (_) {
      return null;
    }
  }

  function resolveCampaignLink(campaign, fallbackUrl, base) {
    const fallback = safeStoreUrl(fallbackUrl, base) || '/collections/bestsellers';
    const name = campaign && typeof campaign.name === 'string' ? campaign.name.trim() : '';
    const url = campaign && safeStoreUrl(campaign.url, base);
    return name && url ? { url, name, active: true } : { url: fallback, name: null, active: false };
  }

  if (typeof module === 'object' && module.exports) module.exports = { validDate, dateInTimezone, resolveCampaign, safeColor, safeImage, replaceSale, safeStoreUrl, resolveCampaignLink };
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__ttSeasonalSales) {
    window.__ttSeasonalSales.refresh(document);
    return;
  }

  const roots = new Map();
  let timer = null;

  function readConfig(root) {
    const script = root.querySelector('[data-tt-seasonal-config]');
    if (!script) return null;
    try {
      const config = JSON.parse(script.textContent);
      return config && typeof config === 'object' && !Array.isArray(config) ? config : null;
    } catch (_) {
      return null;
    }
  }

  function originalText(state, element) {
    if (!state.text.has(element)) state.text.set(element, element.hasAttribute('data-tt-seasonal-original') ? element.getAttribute('data-tt-seasonal-original') : element.textContent);
    return state.text.get(element);
  }

  function restoreProperty(state, property) {
    const original = state.styles.get(property);
    if (original.value) state.root.style.setProperty(property, original.value, original.priority);
    else state.root.style.removeProperty(property);
  }

  function setProperty(state, property, value) {
    if (value) state.root.style.setProperty(property, value);
    else restoreProperty(state, property);
  }

  function setText(element, value) {
    if (element.textContent !== value) element.textContent = value;
  }

  function applyCopy(state, campaign, name, fallback) {
    if (!state.root.hasAttribute('data-tt-seasonal-autocopy')) return;
    const blocked = 'script, style, textarea, input, [contenteditable], [data-tt-seasonal-name], [data-tt-seasonal-emoji], [data-tt-seasonal-decor], [data-tt-seasonal-copy]';
    const walker = document.createTreeWalker(state.root, 4);
    let node;
    while ((node = walker.nextNode())) {
      const parent = node.parentElement;
      if (!parent || parent.closest(blocked) || parent.closest('[data-tt-seasonal-root]') !== state.root) continue;
      const scoped = parent.closest('[data-tt-seasonal-alias]');
      const aliases = [...saleAliases, fallback.name, scoped ? scoped.getAttribute('data-tt-seasonal-alias') : ''];
      const cached = state.copy.get(node);
      const original = cached && node.nodeValue === cached.applied ? cached.original : node.nodeValue;
      const value = campaign ? replaceSale(original, name, aliases) : original;
      if (value !== original || cached) {
        state.copy.set(node, { original, applied: value });
        if (node.nodeValue !== value) node.nodeValue = value;
      }
    }
    state.root.querySelectorAll('[aria-label], [title]').forEach(element => {
      if (element.hasAttribute('data-tt-seasonal-label') || element.closest('[data-tt-seasonal-root]') !== state.root) return;
      ['aria-label', 'title'].forEach(attribute => {
        if (!element.hasAttribute(attribute)) return;
        const values = state.attributes.get(element) || new Map();
        const current = element.getAttribute(attribute);
        const cached = values.get(attribute);
        const original = cached && current === cached.applied ? cached.original : current;
        const value = campaign ? replaceSale(original, name, [...saleAliases, fallback.name]) : original;
        if (value !== original || cached) {
          values.set(attribute, { original, applied: value });
          state.attributes.set(element, values);
          if (current !== value) element.setAttribute(attribute, value);
        }
      });
    });
  }

  function applyLinks(state, campaign, fallback) {
    state.root.querySelectorAll('a[data-tt-seasonal-link]').forEach(element => {
      if (element.closest('[data-tt-seasonal-root]') !== state.root) return;
      const fallbackUrl = element.getAttribute('data-tt-seasonal-fallback-url') || fallback.url;
      const result = resolveCampaignLink(campaign, fallbackUrl, window.location.href);
      if (!state.links.has(element)) state.links.set(element, resolveCampaignLink(null, fallbackUrl, window.location.href).url);
      element.setAttribute('href', result.url);
      if (result.active) return;
      const managedText = '[data-tt-seasonal-name], [data-tt-seasonal-copy], [data-tt-seasonal-emoji], [data-tt-seasonal-decor]';
      const targets = [...element.querySelectorAll(managedText)];
      if (element.matches(managedText)) targets.push(element);
      targets.forEach(target => setText(target, originalText(state, target)));
      const labelTargets = [...element.querySelectorAll('[data-tt-seasonal-label]')];
      if (element.hasAttribute('data-tt-seasonal-label')) labelTargets.push(element);
      labelTargets.forEach(target => {
        const original = state.labels.get(target);
        if (original === null || original === undefined) target.removeAttribute('aria-label');
        else target.setAttribute('aria-label', original);
      });
    });
  }

  function apply(state) {
    const config = readConfig(state.root);
    const result = resolveCampaign(config);
    const campaign = result.campaign;
    const fallback = config && config.fallback && typeof config.fallback === 'object' ? config.fallback : {};
    const name = campaign && typeof campaign.name === 'string' && campaign.name.trim() ? campaign.name.trim() : typeof fallback.name === 'string' ? fallback.name : '';
    const emoji = campaign && typeof campaign.emoji === 'string' && campaign.emoji.trim() ? campaign.emoji.trim() : typeof fallback.emoji === 'string' ? fallback.emoji : '';
    if (campaign) {
      state.root.dataset.ttSeasonalCampaign = String(campaign.id);
      const style = ['stars', 'plain', 'autumn', 'halloween'].includes(campaign.style) ? campaign.style : 'plain';
      state.root.dataset.ttSeasonalStyle = style;
      Object.entries(paletteProperties).forEach(([key, property]) => setProperty(state, property, safeColor(campaign[key])));
      const desktop = safeImage(campaign.imageDesktop, document.baseURI);
      const mobile = safeImage(campaign.imageMobile, document.baseURI) || desktop;
      setProperty(state, '--tts-image-desktop', desktop);
      setProperty(state, '--tts-image-mobile', mobile);
    } else {
      delete state.root.dataset.ttSeasonalCampaign;
      delete state.root.dataset.ttSeasonalStyle;
      managedProperties.forEach(property => restoreProperty(state, property));
    }
    state.root.querySelectorAll('[data-tt-seasonal-name]').forEach(element => {
      const original = originalText(state, element);
      let value = campaign ? name || original : original;
      if (campaign && element.hasAttribute('data-uppercase') && element.getAttribute('data-uppercase') !== 'false') value = value.toUpperCase();
      setText(element, value);
    });
    state.root.querySelectorAll('[data-tt-seasonal-emoji], [data-tt-seasonal-decor]').forEach(element => {
      const original = originalText(state, element);
      setText(element, campaign ? emoji || original : original);
    });
    state.root.querySelectorAll('[data-tt-seasonal-label]').forEach(element => {
      if (!state.labels.has(element)) state.labels.set(element, element.getAttribute('aria-label'));
      const original = state.labels.get(element);
      if (campaign) element.setAttribute('aria-label', element.getAttribute('data-tt-seasonal-label').replaceAll('{sale}', name));
      else if (original === null) element.removeAttribute('aria-label');
      else element.setAttribute('aria-label', original);
    });
    state.root.querySelectorAll('[data-tt-seasonal-copy]').forEach(element => {
      const original = originalText(state, element);
      setText(element, campaign ? element.getAttribute('data-tt-seasonal-copy').replaceAll('{sale}', name) : original);
    });
    applyCopy(state, campaign, name, fallback);
    applyLinks(state, campaign, fallback);
  }

  function update() {
    for (const [root, state] of roots) {
      if (!root.isConnected) {
        roots.delete(root);
        continue;
      }
      try {
        apply(state);
      } catch (_) {
        delete root.dataset.ttSeasonalCampaign;
        delete root.dataset.ttSeasonalStyle;
        managedProperties.forEach(property => restoreProperty(state, property));
        state.text.forEach((value, element) => setText(element, value));
        state.links.forEach((value, element) => element.setAttribute('href', value));
        state.labels.forEach((value, element) => value === null ? element.removeAttribute('aria-label') : element.setAttribute('aria-label', value));
        state.copy.forEach((value, node) => { if (node.nodeValue === value.applied) node.nodeValue = value.original; });
        state.attributes.forEach((values, element) => values.forEach((value, attribute) => { if (element.getAttribute(attribute) === value.applied) element.setAttribute(attribute, value.original); }));
      }
    }
    syncTimer();
  }

  function syncTimer() {
    if (document.visibilityState === 'hidden' || roots.size === 0) {
      if (timer !== null) window.clearInterval(timer);
      timer = null;
    } else if (timer === null) timer = window.setInterval(update, 30000);
  }

  function refresh(scope) {
    const candidates = [];
    if (scope.matches && scope.matches('[data-tt-seasonal-root]')) candidates.push(scope);
    if (scope.querySelectorAll) candidates.push(...scope.querySelectorAll('[data-tt-seasonal-root]'));
    candidates.forEach(root => {
      if (roots.has(root)) return;
      roots.set(root, {
        root,
        text: new Map(),
        labels: new Map(),
        links: new Map(),
        copy: new Map(),
        attributes: new Map(),
        styles: new Map(managedProperties.map(property => [property, { value: root.style.getPropertyValue(property), priority: root.style.getPropertyPriority(property) }]))
      });
    });
    update();
  }

  window.__ttSeasonalSales = { refresh };
  document.addEventListener('shopify:section:load', event => refresh(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const root of roots.keys()) if (root === event.target || event.target.contains(root)) roots.delete(root);
    syncTimer();
  });
  document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' ? syncTimer() : refresh(document));
  window.addEventListener('pageshow', () => refresh(document));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => refresh(document), { once: true });
  else refresh(document);
})();

